// The minimap (2026-10-26, ui2): the whole table in a small canvas in the HUD. The ground (roads, the river, grass, forest) is
// drawn once a match from the sim's map; on top, redrawn five times a second (not every frame), the center's ring, the
// four barracks in their team colors, every soldier as a dot in his team's color (heroes bigger, ringed), and your
// camera's view as an outline. Tap or drag on it to move the camera there.
import { HERO_RULES } from '../../sim/defs';
import { NEUTRAL, SIZE, STATE, type Sim } from '../../sim/sim';
import { riverHalf, riverMid } from '../river';
import { TEAM } from './data';

/** Milliseconds between redraws: dots move about a soldier's width in that time, so the eye reads it as smooth enough. */
export const MINIMAP_EVERY = 200;
const NEUTRAL_COLOR = '#8f8f99';
/** The ground's colors (sRGB 0..255). SUMMER_GROUND is the map as drawn for summer, lighter than the world so dots read. */
export type Ground = Record<'road' | 'grass' | 'forest' | 'water', readonly [number, number, number]>;
export const SUMMER_GROUND: Ground = { road: [216, 192, 138], grass: [134, 179, 92], forest: [72, 118, 64], water: [118, 176, 204] };
const SNOW = [238, 242, 248] as const;
/** How much of the snow lying shows on each: open grass most, under the trees less, none on the trodden roads, which
 *  the map exists to show (at 0.3 a winter road was the same pale as the fields, uiart walk) */
const SNOW_SHOWS = { road: 0, grass: 0.85, forest: 0.45, water: 0 } as const;

/** The map's ground for this match: each summer color moved as far as the season moved the world's matching palette
 *  color (`now` over `summer`, channel by channel), then under the snow lying (`cover`, 0..1; sky.ts). Winter reads
 *  white with dark woods, autumn olive, as the table does (seasons pass, uiart 2026-10-26). */
export function groundColors(now: Ground, summer: Ground, cover: number): Ground {
  const out = {} as Record<keyof Ground, [number, number, number]>;
  for (const k of Object.keys(SUMMER_GROUND) as (keyof Ground)[]) {
    const s = SNOW_SHOWS[k] * Math.max(0, Math.min(1, cover));
    out[k] = SUMMER_GROUND[k].map((v, i) => {
      const moved = Math.min(255, v * (now[k][i] + 1) / (summer[k][i] + 1));
      return Math.round(moved + (SNOW[i] - moved) * s);
    }) as [number, number, number];
  }
  return out;
}

/** The parts of a 2D context the minimap draws with (a stub stands in for it in tests). */
export type Ctx = Pick<CanvasRenderingContext2D, 'clearRect' | 'drawImage' | 'fillRect' | 'beginPath' | 'arc' | 'moveTo' | 'lineTo' | 'closePath' | 'fill' | 'stroke'>
  & { fillStyle: string | CanvasGradient | CanvasPattern; strokeStyle: string | CanvasGradient | CanvasPattern; lineWidth: number; globalAlpha: number; lineJoin: CanvasLineJoin };

/** World (x, z) to minimap pixels for a map `px` pixels wide, and back. Screen up is the table's far side (-z). */
export const toMap = (x: number, z: number, px: number) => [(x / SIZE) * px, (z / SIZE) * px] as const;
export const fromMap = (u: number, v: number, px: number) => [Math.max(0, Math.min(SIZE, (u / px) * SIZE)), Math.max(0, Math.min(SIZE, (v / px) * SIZE))] as const;

/** One pixel a cell: road (bridges included), the painted river (river.ts, visual only), open grass or forest. */
export function groundPixels(sim: Sim, ground: Ground = SUMMER_GROUND) {
  const px = new Uint8ClampedArray(SIZE * SIZE * 4);
  for (let k = 0; k < SIZE * SIZE; k++) {
    const x = (k % SIZE) + 0.5, z = Math.floor(k / SIZE) + 0.5, a = Math.atan2(z - SIZE / 2, x - SIZE / 2);
    const wet = Math.abs(Math.hypot(x - SIZE / 2, z - SIZE / 2) - riverMid(a)) <= riverHalf(a);
    const c = sim.road[k] ? ground.road : wet ? ground.water : sim.blocked[k] ? ground.forest : ground.grass;
    px.set([c[0], c[1], c[2], 255], k * 4);
  }
  return px;
}

/** Draw the map's live layer. `view` is the camera's footprint on the table, four corners as x, z pairs. */
export function paint(g: Ctx, sim: Sim, view: readonly number[], px: number, ground: CanvasImageSource | null, me: number) {
  const k = px / SIZE;
  g.clearRect(0, 0, px, px);
  if (ground) g.drawImage(ground, 0, 0, px, px);
  // the center: its mana ring
  const [cx, cy] = toMap(sim.center.x, sim.center.y, px);
  g.beginPath(); g.arc(cx, cy, HERO_RULES.centerRadius * k, 0, Math.PI * 2);
  g.globalAlpha = 0.28; g.fillStyle = '#fffaf0'; g.fill(); g.globalAlpha = 1;
  g.lineWidth = Math.max(1, px / 120); g.strokeStyle = '#fffaf0'; g.stroke();
  g.beginPath(); g.arc(cx, cy, Math.max(2.5, 3.2 * k), 0, Math.PI * 2); g.fillStyle = '#1f2a4a'; g.fill();
  // the barracks
  const b = Math.max(7, 7 * k);
  sim.bases.forEach((base, p) => {
    const [x, y] = toMap(base.x, base.y, px), out = sim.players[p].out;
    g.globalAlpha = out ? 0.35 : 1;
    g.fillStyle = '#1f2a4a'; g.fillRect(x - b / 2 - 1.5, y - b / 2 - 1.5, b + 3, b + 3);
    g.fillStyle = TEAM[p]; g.fillRect(x - b / 2, y - b / 2, b, b);
    if (p === me) { g.fillStyle = '#fffaf0'; g.fillRect(x - b / 6, y - b / 6, b / 3, b / 3); }
  });
  g.globalAlpha = 1;
  // soldiers: one pass a team so the fill changes five times, not once a dot; the center's waves first, under the teams
  const d = Math.max(2, px / 64);
  for (const t of [NEUTRAL, 0, 1, 2, 3]) {
    g.fillStyle = t === NEUTRAL ? NEUTRAL_COLOR : TEAM[t];
    for (let i = 0; i < sim.state.length; i++) {
      if (sim.state[i] === STATE.dead || sim.team[i] !== t || sim.heroOf[i] >= 0) continue;
      const [x, y] = toMap(sim.x[i], sim.y[i], px);
      g.fillRect(x - d / 2, y - d / 2, d, d);
    }
  }
  // heroes: bigger, ringed in ivory so one is found among its army
  sim.players.forEach((pl, p) => {
    const i = pl.hero?.i ?? -1;
    if (i < 0) return;
    const [x, y] = toMap(sim.x[i], sim.y[i], px);
    g.beginPath(); g.arc(x, y, d * 1.4, 0, Math.PI * 2); g.fillStyle = TEAM[p]; g.fill();
    g.lineWidth = Math.max(1, d / 2); g.strokeStyle = p === me ? '#f2c443' : '#fffaf0'; g.stroke();
  });
  // the camera's view, ivory over a navy edge so it reads on sand and forest alike
  if (view.length === 8) {
    g.beginPath();
    // drawn as it is: corners past the table (the far edge of a tilted view) fall off the canvas, which clips them, so
    // each edge still crosses the map border where the screen's does (clamping corners bent the edges, Astra F3)
    for (let c = 0; c < 4; c++) { const [x, y] = toMap(view[c * 2], view[c * 2 + 1], px); if (c) g.lineTo(x, y); else g.moveTo(x, y); }
    g.closePath(); g.lineJoin = 'round';
    g.lineWidth = Math.max(2.5, px / 48); g.strokeStyle = 'rgb(31 42 74 / .55)'; g.stroke();
    g.lineWidth = Math.max(1.2, px / 110); g.strokeStyle = '#fffaf0'; g.stroke();
  }
}

/** `ground`: this match's ground colors (main.ts: the season and the snow lying); the map repaints when they change. */
type Hooks = { view: () => number[]; jump: (x: number, z: number) => void; ground?: () => Ground };

export class Minimap {
  readonly el = document.createElement('div');
  private cv = document.createElement('canvas');
  private ground: HTMLCanvasElement | null = null;
  private groundKey = '';
  /** The colors the ground was last painted with (?qa=1 publishes them: the walk checks winter reaches the map). */
  colors: Ground | null = null;
  private sim: Sim | null = null;
  private me = -1;
  private last = -1e9;
  /** Redraws so far (?qa=1 publishes it: the shots check the map is live and not redrawn every frame). */
  draws = 0;

  constructor(readonly hooks: Hooks) {
    this.el.className = 'minimap';
    this.el.setAttribute('role', 'img');
    this.el.setAttribute('aria-label', 'Map of the table. Tap it to move the camera there.');
    this.el.append(this.cv);
    let down = -1;
    const go = (e: PointerEvent) => {
      const r = this.cv.getBoundingClientRect(), [x, z] = fromMap(e.clientX - r.left, e.clientY - r.top, r.width);
      this.hooks.jump(x, z);
    };
    // a tap redraws at once; a drag keeps the throttle (the camera moves every frame, the outline catches up, Astra F1)
    this.cv.addEventListener('pointerdown', (e) => { down = e.pointerId; this.cv.setPointerCapture(e.pointerId); go(e); this.last = -1e9; e.preventDefault(); });
    this.cv.addEventListener('pointermove', (e) => { if (e.pointerId === down) go(e); });
    const up = (e: PointerEvent) => { if (e.pointerId === down) down = -1; };
    this.cv.addEventListener('pointerup', up); this.cv.addEventListener('pointercancel', up);
  }

  setSim(sim: Sim, me: number) { this.sim = sim; this.me = me; this.ground = null; this.last = -1e9; }

  /** Called every frame by the HUD; draws only every MINIMAP_EVERY ms. */
  update(now: number) {
    if (!this.sim || now - this.last < MINIMAP_EVERY) return;
    this.last = now;
    const css = this.cv.clientWidth;
    if (!css) return; // hidden
    const px = Math.round(css * Math.min(2, devicePixelRatio || 1));
    if (this.cv.width !== px) { this.cv.width = this.cv.height = px; }
    const colors = this.hooks.ground?.() ?? SUMMER_GROUND, key = Object.values(colors).join();
    if (!this.ground || key !== this.groundKey) {
      this.ground ??= document.createElement('canvas'); this.ground.width = this.ground.height = SIZE; this.groundKey = key; this.colors = colors;
      this.ground.getContext('2d')!.putImageData(new ImageData(groundPixels(this.sim, colors), SIZE, SIZE), 0, 0);
    }
    const g = this.cv.getContext('2d')!;
    g.imageSmoothingEnabled = true;
    paint(g, this.sim, this.hooks.view(), px, this.ground, this.me);
    this.draws++;
  }
}
